import React, { useCallback, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@vehix/portal/components/ui/table';
import { Button } from '@vehix/portal/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@vehix/portal/components/ui/select';
import { Card } from '@vehix/portal/components/ui/card';
import { Badge } from '@vehix/portal/components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@vehix/portal/components/ui/popover';
import {
  Loader2,
  Play,
  Ban,
  Activity,
  CheckCircle,
  XCircle,
  DollarSign,
  ExternalLink,
  Info,
  Copy
} from 'lucide-react';
import { PipelineStatusBadge } from './PipelineStatusBadge';
import { usePipelines, useCancelPipeline } from '../../hooks/usePipelineOrchestrator';
import { useToast } from '@vehix/shared/admin/portal-bridge';
import { getConfigBadges, formatRelativeTime } from '../../utils/pipelineFormat';
import { getZoneSummary, humanizeSlug } from '../../utils/pipelineZones';
import {
  PipelineRangeFilter,
  applyRange,
  isValidRange,
  widerRange,
  DEFAULT_RANGE,
  type RangeKey
} from './PipelineRangeFilter';
import type {
  PipelineListParams,
  PipelineStatus,
  PipelineDetail
} from '../../services/pipelineOrchestrator';
import type { Tenant } from '@vehix/portal/services/api/tenants';

// ============================================================================
// HELPERS
// ============================================================================

function formatDuration(seconds: number | null): string {
  if (seconds === null || seconds === undefined) return '-';
  const s = Math.round(seconds);
  if (s < 60) return `${s}s`;
  const mins = Math.floor(s / 60);
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  const remainMins = mins % 60;
  return `${hrs}h ${remainMins}m`;
}

function humanizePipelineName(name: string): string {
  return name.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function formatTimestamp(ts: string | null): string {
  if (!ts) return '-';
  const d = new Date(ts);
  const now = new Date();
  const isToday = d.toDateString() === now.toDateString();
  if (isToday) return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  return d.toLocaleDateString([], {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

// ============================================================================
// STATS CARDS
// ============================================================================

interface StatsCardsProps {
  running: number;
  completed: number;
  failed: number;
  totalCost: number;
}

const StatsCards: React.FC<StatsCardsProps> = ({ running, completed, failed, totalCost }) => (
  <div className='grid grid-cols-2 md:grid-cols-4 gap-3'>
    <Card className='p-3 flex items-center gap-3'>
      <Activity className='h-5 w-5 text-blue-500' />
      <div>
        <p className='text-lg font-bold'>{running}</p>
        <p className='text-xs text-muted-foreground'>Running</p>
      </div>
    </Card>
    <Card className='p-3 flex items-center gap-3'>
      <CheckCircle className='h-5 w-5 text-green-500' />
      <div>
        <p className='text-lg font-bold'>{completed}</p>
        <p className='text-xs text-muted-foreground'>Completed</p>
      </div>
    </Card>
    <Card className='p-3 flex items-center gap-3'>
      <XCircle className='h-5 w-5 text-red-500' />
      <div>
        <p className='text-lg font-bold'>{failed}</p>
        <p className='text-xs text-muted-foreground'>Failed</p>
      </div>
    </Card>
    <Card className='p-3 flex items-center gap-3'>
      <DollarSign className='h-5 w-5 text-primary' />
      <div>
        <p className='text-lg font-bold'>${totalCost.toFixed(2)}</p>
        <p className='text-xs text-muted-foreground'>Total Cost</p>
      </div>
    </Card>
  </div>
);

// ============================================================================
// MAIN COMPONENT
// ============================================================================

interface PipelineDashboardProps {
  tenantId?: string | undefined;
  tenants?: Tenant[] | undefined;
}

// eslint-disable-next-line max-lines-per-function
export const PipelineDashboard: React.FC<PipelineDashboardProps> = ({ tenantId, tenants }) => {
  const navigate = useNavigate();
  const { toast } = useToast();

  /**
   * The orchestrator has no date parameters — `PipelineListParams` carries only
   * tenant, status, trigger and offset paging — and that service lives outside
   * this repo. But the table is small (261 runs all-time, 202 in the last three
   * months, 28 tenants), so the whole window is fetched once and scoped on the
   * client. Revisit if run volume ever approaches this ceiling.
   */
  const FETCH_LIMIT = 300;
  const pageSize = 25;

  // Filters live in the URL so a view survives a refresh and can be shared.
  const [searchParams, setSearchParams] = useSearchParams();
  const statusFilter = searchParams.get('status') ?? '__all__';
  const rangeParam = searchParams.get('range');
  const range: RangeKey = isValidRange(rangeParam) ? rangeParam : DEFAULT_RANGE;
  const currentPage = Math.max(1, Number(searchParams.get('page') ?? '1') || 1);

  const setParams = useCallback(
    (changes: Record<string, string | undefined>) => {
      setSearchParams(
        prev => {
          const next = new URLSearchParams(prev);
          for (const [k, v] of Object.entries(changes)) {
            if (v === undefined) next.delete(k);
            else next.set(k, v);
          }
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const params: PipelineListParams = {
    ...(tenantId && { tenant_id: tenantId }),
    ...(statusFilter !== '__all__' && { status: statusFilter as PipelineStatus }),
    // trigger_mode filter disabled — API does not support filtering by trigger type yet
    limit: FETCH_LIMIT
  };

  const { data, isLoading, error } = usePipelines(params);
  const cancelPipeline = useCancelPipeline();

  const fetched = useMemo(() => data?.pipelines ?? [], [data?.pipelines]);
  const total = data?.total || 0;
  /** True when the server holds more runs than one page of FETCH_LIMIT. */
  const truncated = total > fetched.length;

  /** Filtered to the range and ordered newest first. */
  const scoped = useMemo(() => applyRange(fetched, range), [fetched, range]);
  const wider = widerRange(range);

  const totalPages = Math.max(1, Math.ceil(scoped.length / pageSize));
  const pageStart = (currentPage - 1) * pageSize;
  const pipelines = useMemo(
    () => scoped.slice(pageStart, pageStart + pageSize),
    [scoped, pageStart]
  );

  // Landing on a page past the end of a narrowed result set renders an empty
  // table rather than results.
  useEffect(() => {
    if (currentPage > totalPages) setParams({ page: undefined });
  }, [currentPage, totalPages, setParams]);

  /**
   * Stats cover the full scoped set, not the current page. Previously these
   * counted only the 25 rows on screen, so "Failed: 3" silently meant "3 failed
   * on page 1 of 9".
   */
  const stats = useMemo(() => {
    return {
      running: scoped.filter(p => p.status === 'running' || p.status === 'pending').length,
      completed: scoped.filter(p => p.status === 'completed').length,
      failed: scoped.filter(p => p.status === 'failed').length,
      totalCost: scoped.reduce((sum, p) => sum + (p.total_cost_usd || 0), 0)
    };
  }, [scoped]);

  const tenantLookup = useMemo(() => {
    const map = new Map<string, string>();
    for (const t of tenants || []) {
      map.set(t.id, t.tenant_name);
    }
    return map;
  }, [tenants]);

  const handleCancel = (pipelineId: string) => {
    toast({ title: 'Cancelling pipeline...', variant: 'default' });
    cancelPipeline.mutate(pipelineId, {
      onSuccess: () => {
        toast({ title: 'Pipeline cancelled', variant: 'default' });
      },
      onError: () => {
        toast({ title: 'Failed to cancel pipeline', variant: 'destructive' });
      }
    });
  };

  if (error) {
    return (
      <div className='text-center text-muted-foreground py-8'>
        <XCircle className='h-8 w-8 mx-auto mb-2 text-red-500' />
        <p className='text-sm'>Failed to load pipelines</p>
        <p className='text-xs text-muted-foreground mt-1'>
          {error instanceof Error ? error.message : 'Unknown error'}
        </p>
      </div>
    );
  }

  // eslint-disable-next-line max-lines-per-function
  const renderPipelineRow = (pipeline: PipelineDetail, index: number) => (
    <TableRow
      key={pipeline.id}
      className={`cursor-pointer hover:bg-muted/50 ${
        index % 2 === 0 ? 'bg-background' : 'bg-muted/30'
      }`}
      onClick={() => navigate(`/workflow/pipelines/${pipeline.id}`)}
    >
      <TableCell>
        <PipelineStatusBadge status={pipeline.status} size='sm' />
      </TableCell>
      <TableCell>
        <div>
          <p className='text-sm font-medium truncate max-w-[200px]'>
            {humanizePipelineName(pipeline.pipeline_name)}
          </p>
          <p className='text-xs text-muted-foreground'>
            {tenantLookup.get(pipeline.tenant_id) || pipeline.tenant_id}
          </p>
          {(() => {
            const badges = getConfigBadges(pipeline);
            return badges.length > 0 ? (
              <div className='flex gap-1 mt-0.5'>
                {badges.map(b => (
                  <Badge
                    key={b.label}
                    variant='outline'
                    className={`text-[9px] px-1 py-0 h-4 ${b.className}`}
                  >
                    {b.label}
                  </Badge>
                ))}
              </div>
            ) : null;
          })()}
        </div>
      </TableCell>
      <TableCell className='hidden lg:table-cell max-w-[200px]'>
        {(() => {
          const zones = getZoneSummary(pipeline.config);
          if (!zones) {
            return (
              <span className='text-sm'>
                {pipeline.completed_zones}/{pipeline.total_zones}
              </span>
            );
          }
          const zoneNames = Array.from(zones.keys());
          const collectors = Array.from(new Set(Array.from(zones.values()).flat()));
          // Cap at 3 zone names visible to keep the column from exploding;
          // overflow shown as "+N more". Full zone list visible in the
          // popup or detail page. Same approach for collectors.
          const VISIBLE = 3;
          const visibleZones = zoneNames.slice(0, VISIBLE).map(humanizeSlug).join(', ');
          const extraZones = zoneNames.length - VISIBLE;
          const visibleCollectors = collectors.slice(0, VISIBLE).map(humanizeSlug).join(', ');
          const extraCollectors = collectors.length - VISIBLE;
          return (
            <div>
              <p
                className='text-sm font-medium truncate'
                title={zoneNames.map(humanizeSlug).join(', ')}
              >
                {visibleZones}
                {extraZones > 0 && (
                  <span className='text-muted-foreground'> +{extraZones} more</span>
                )}
              </p>
              <p
                className='text-[10px] text-muted-foreground truncate'
                title={collectors.map(humanizeSlug).join(', ')}
              >
                {visibleCollectors}
                {extraCollectors > 0 && <span> +{extraCollectors} more</span>}
              </p>
            </div>
          );
        })()}
      </TableCell>
      <TableCell className='hidden xl:table-cell'>
        <span className='text-xs capitalize'>
          {pipeline.effective_run_mode || pipeline.run_mode}
        </span>
      </TableCell>
      <TableCell className='hidden md:table-cell'>
        {/* Relative reads faster when scanning for "what just ran"; the exact
            timestamp stays available on hover. */}
        <span
          className='whitespace-nowrap text-xs'
          title={formatTimestamp(pipeline.started_at || pipeline.requested_at)}
        >
          {formatRelativeTime(pipeline.started_at || pipeline.requested_at)}
        </span>
      </TableCell>
      <TableCell className='hidden md:table-cell'>
        <span className='text-xs'>{formatDuration(pipeline.duration_seconds)}</span>
      </TableCell>
      <TableCell className='hidden xl:table-cell text-right'>
        <span className='text-xs'>{(pipeline.total_vins_processed || 0).toLocaleString()}</span>
      </TableCell>
      <TableCell className='hidden xl:table-cell text-right'>
        <span className='text-xs'>
          {pipeline.total_cost_usd > 0 ? `$${pipeline.total_cost_usd.toFixed(2)}` : '-'}
        </span>
      </TableCell>
      <TableCell>
        {/* Info-icon trigger opens a popover with all run metadata. Mirrors
            the Zone List Info-popover pattern (ZoneSetupTab.tsx) but adds
            click-to-copy on the high-cardinality fields (run id, tenant id,
            full timestamps, watcher emails) so admins can paste them into
            logs / Sentry / support tickets without typing. The trigger is
            stopPropagation'd so opening the popover doesn't navigate to
            the detail page. */}
        {(() => {
          const cfg = pipeline.config ?? ({} as PipelineDetail['config']);
          const dedupOff = cfg.enable_dedup === false || cfg.force_reprocess === true;
          const flags: string[] = [];
          if (cfg.skip_vin_processing) flags.push('skip-VINs');
          if (dedupOff) flags.push('no-dedup');

          const requestedAt = pipeline.requested_at
            ? new Date(pipeline.requested_at).toLocaleString()
            : null;
          const startedAt = pipeline.started_at
            ? new Date(pipeline.started_at).toLocaleString()
            : null;
          const completedAt = pipeline.completed_at
            ? new Date(pipeline.completed_at).toLocaleString()
            : null;

          const tenantName = tenantLookup.get(pipeline.tenant_id) || pipeline.tenant_id;
          const emails = cfg.notification_emails ?? [];

          const copy = async (label: string, value: string) => {
            try {
              await navigator.clipboard.writeText(value);
              toast({ title: `${label} copied`, description: value });
            } catch {
              toast({
                title: 'Copy failed',
                description: 'Clipboard access denied',
                variant: 'destructive'
              });
            }
          };

          return (
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant='ghost'
                  size='sm'
                  className='h-8 w-8 p-0'
                  onClick={e => e.stopPropagation()}
                  title='Run metadata'
                >
                  <Info className='h-4 w-4' />
                </Button>
              </PopoverTrigger>
              <PopoverContent side='left' className='w-80' onClick={e => e.stopPropagation()}>
                <div className='space-y-3 text-sm'>
                  {/* Identifiers section — the high-value-for-copy fields */}
                  <div className='flex items-start justify-between gap-2'>
                    <span className='text-muted-foreground text-xs pt-1'>Run ID</span>
                    <div className='flex items-center gap-1 min-w-0'>
                      <code
                        className='text-[10px] bg-muted px-2 py-0.5 rounded truncate font-mono'
                        title={pipeline.id}
                      >
                        {pipeline.id}
                      </code>
                      <Button
                        variant='ghost'
                        size='sm'
                        className='h-6 w-6 p-0 flex-shrink-0'
                        onClick={() => copy('Run ID', pipeline.id)}
                      >
                        <Copy className='h-3 w-3' />
                      </Button>
                    </div>
                  </div>
                  <div className='flex items-start justify-between gap-2'>
                    <span className='text-muted-foreground text-xs pt-1'>Tenant</span>
                    <div className='flex items-center gap-1 min-w-0'>
                      <span className='text-xs font-medium truncate' title={pipeline.tenant_id}>
                        {tenantName}
                      </span>
                      <Button
                        variant='ghost'
                        size='sm'
                        className='h-6 w-6 p-0 flex-shrink-0'
                        onClick={() => copy('Tenant ID', pipeline.tenant_id)}
                      >
                        <Copy className='h-3 w-3' />
                      </Button>
                    </div>
                  </div>

                  {/* Lifecycle timestamps — full ISO via copy button */}
                  <div className='border-t pt-2 space-y-1'>
                    {requestedAt && (
                      <div className='flex items-center justify-between gap-2'>
                        <span className='text-muted-foreground text-xs'>Requested</span>
                        <div className='flex items-center gap-1'>
                          <span className='text-xs'>{requestedAt}</span>
                          <Button
                            variant='ghost'
                            size='sm'
                            className='h-6 w-6 p-0'
                            onClick={() => copy('Requested at', pipeline.requested_at)}
                          >
                            <Copy className='h-3 w-3' />
                          </Button>
                        </div>
                      </div>
                    )}
                    {startedAt && pipeline.started_at && (
                      <div className='flex items-center justify-between gap-2'>
                        <span className='text-muted-foreground text-xs'>Started</span>
                        <div className='flex items-center gap-1'>
                          <span className='text-xs'>{startedAt}</span>
                          <Button
                            variant='ghost'
                            size='sm'
                            className='h-6 w-6 p-0'
                            onClick={() =>
                              pipeline.started_at && copy('Started at', pipeline.started_at)
                            }
                          >
                            <Copy className='h-3 w-3' />
                          </Button>
                        </div>
                      </div>
                    )}
                    {completedAt && pipeline.completed_at && (
                      <div className='flex items-center justify-between gap-2'>
                        <span className='text-muted-foreground text-xs'>Completed</span>
                        <div className='flex items-center gap-1'>
                          <span className='text-xs'>{completedAt}</span>
                          <Button
                            variant='ghost'
                            size='sm'
                            className='h-6 w-6 p-0'
                            onClick={() =>
                              pipeline.completed_at && copy('Completed at', pipeline.completed_at)
                            }
                          >
                            <Copy className='h-3 w-3' />
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Execution config */}
                  <div className='border-t pt-2 space-y-1'>
                    <div className='flex items-center justify-between'>
                      <span className='text-muted-foreground text-xs'>Run mode</span>
                      <span className='text-xs capitalize'>
                        {pipeline.effective_run_mode || pipeline.run_mode || '—'}
                        {pipeline.effective_run_mode &&
                          pipeline.run_mode &&
                          pipeline.effective_run_mode !== pipeline.run_mode && (
                            <span className='text-muted-foreground'>
                              {' '}
                              (req: {pipeline.run_mode})
                            </span>
                          )}
                      </span>
                    </div>
                    {cfg.vin_processor_type && (
                      <div className='flex items-center justify-between'>
                        <span className='text-muted-foreground text-xs'>VIN processor</span>
                        <span className='text-xs capitalize'>{cfg.vin_processor_type}</span>
                      </div>
                    )}
                    {flags.length > 0 && (
                      <div className='flex items-center justify-between gap-2'>
                        <span className='text-muted-foreground text-xs'>Flags</span>
                        <div className='flex flex-wrap gap-1 justify-end'>
                          {flags.map(f => (
                            <Badge
                              key={f}
                              variant='outline'
                              className='text-[10px] px-1.5 py-0 h-5 border-amber-300 bg-amber-50 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-900'
                            >
                              {f}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Notification watchers — list with copy-all */}
                  {emails.length > 0 && (
                    <div className='border-t pt-2'>
                      <div className='flex items-center justify-between mb-1'>
                        <span className='text-muted-foreground text-xs'>
                          Watchers ({emails.length})
                        </span>
                        <Button
                          variant='ghost'
                          size='sm'
                          className='h-6 w-6 p-0'
                          onClick={() => copy('Watcher emails', emails.join(', '))}
                          title='Copy all emails (comma-separated)'
                        >
                          <Copy className='h-3 w-3' />
                        </Button>
                      </div>
                      <div className='flex flex-wrap gap-1'>
                        {emails.map(e => (
                          <Badge key={e} variant='outline' className='text-[10px] px-1.5 py-0 h-5'>
                            {e}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </PopoverContent>
            </Popover>
          );
        })()}
      </TableCell>
      <TableCell className='text-right'>
        <div className='flex justify-end gap-1'>
          <Button
            variant='ghost'
            size='sm'
            className='h-7 px-2'
            onClick={e => {
              e.stopPropagation();
              navigate(`/workflow/pipelines/${pipeline.id}`);
            }}
          >
            <ExternalLink className='h-3 w-3' />
          </Button>
          {(pipeline.status === 'running' || pipeline.status === 'pending') && (
            <Button
              variant='ghost'
              size='sm'
              className='h-7 px-2 text-red-500 hover:text-red-600'
              onClick={e => {
                e.stopPropagation();
                handleCancel(pipeline.id);
              }}
            >
              <Ban className='h-3 w-3' />
            </Button>
          )}
        </div>
      </TableCell>
    </TableRow>
  );

  return (
    <div className='space-y-4'>
      {/* Stats */}
      <StatsCards {...stats} />

      {/* Filters */}
      <div className='flex flex-wrap items-center gap-3'>
        <PipelineRangeFilter
          value={range}
          onChange={v => setParams({ range: v, page: undefined })}
        />

        <Select
          value={statusFilter}
          onValueChange={v =>
            setParams({ status: v === '__all__' ? undefined : v, page: undefined })
          }
        >
          <SelectTrigger className='h-8 w-[150px] text-xs'>
            <SelectValue placeholder='Status' />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value='__all__'>All Statuses</SelectItem>
            <SelectItem value='pending'>Pending</SelectItem>
            <SelectItem value='running'>Running</SelectItem>
            <SelectItem value='completed'>Completed</SelectItem>
            <SelectItem value='failed'>Failed</SelectItem>
            <SelectItem value='partial_success'>Partial</SelectItem>
            <SelectItem value='cancelled'>Cancelled</SelectItem>
          </SelectContent>
        </Select>

        {/* Result count. Says what it is counting, so no number is ambiguous. */}
        <div className='ml-auto text-xs text-muted-foreground'>
          {scoped.length > 0
            ? `Showing ${pageStart + 1}-${Math.min(pageStart + pageSize, scoped.length)} ` +
              `of ${scoped.length} run${scoped.length === 1 ? '' : 's'}`
            : 'No runs in range'}
          {truncated && (
            <span
              title={`The service reports ${total} runs; the most recent ${fetched.length} were loaded.`}
            >
              {' '}
              · newest {fetched.length} of {total} loaded
            </span>
          )}
        </div>
      </div>

      {/* Table */}
      {isLoading ? (
        <div className='flex items-center justify-center py-12'>
          <Loader2 className='h-6 w-6 animate-spin text-muted-foreground' />
        </div>
      ) : scoped.length === 0 ? (
        <div className='py-12 text-center text-muted-foreground'>
          <Play className='mx-auto mb-2 h-8 w-8 opacity-40' />
          <p className='text-sm'>
            No runs {tenantId ? 'for this tenant ' : ''}in the selected range
          </p>
          {/* A tight default scope makes a widening prompt essential — without
              it an empty table reads as "this page is broken". */}
          {wider ? (
            <Button
              variant='link'
              size='sm'
              className='text-xs'
              onClick={() => setParams({ range: wider.key, page: undefined })}
            >
              Try {wider.label}
            </Button>
          ) : (
            <p className='mt-1 text-xs'>Start a new pipeline to see it here</p>
          )}
        </div>
      ) : (
        <div className='-mx-4 overflow-x-auto px-4 sm:mx-0 sm:px-0'>
          <Table>
            <TableHeader>
              <TableRow className='border-b bg-muted'>
                <TableHead className='w-[100px]'>Status</TableHead>
                <TableHead>Pipeline</TableHead>
                <TableHead className='hidden lg:table-cell'>Zones</TableHead>
                <TableHead className='hidden xl:table-cell'>Mode</TableHead>
                <TableHead className='hidden md:table-cell'>Started</TableHead>
                <TableHead className='hidden md:table-cell'>Duration</TableHead>
                <TableHead className='hidden xl:table-cell text-right'>VINs</TableHead>
                <TableHead className='hidden xl:table-cell text-right'>Cost</TableHead>
                <TableHead className='w-[60px]'>Info</TableHead>
                <TableHead className='text-right'>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>{pipelines.map(renderPipelineRow)}</TableBody>
          </Table>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className='flex items-center justify-center gap-2'>
          <Button
            variant='outline'
            size='sm'
            disabled={currentPage <= 1}
            onClick={() => setParams({ page: String(Math.max(1, currentPage - 1)) })}
          >
            Previous
          </Button>
          <span className='text-xs tabular-nums text-muted-foreground'>
            Page {currentPage} of {totalPages}
          </span>
          <Button
            variant='outline'
            size='sm'
            disabled={currentPage >= totalPages}
            onClick={() => setParams({ page: String(Math.min(totalPages, currentPage + 1)) })}
          >
            Next
          </Button>
        </div>
      )}
    </div>
  );
};
